import { useEffect, useState } from "react";
import { Lock, Eye, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

/**
 * Vault auto-lock — when enabled in /settings, the Vault and Capsule screens
 * require the user to re-confirm their KeepSoul password before any item is
 * shown, both at first entry AND after N minutes of inactivity.
 *
 * Storage:
 *  - localStorage `ks_vault_lock_enabled` ("true" / "false")
 *  - localStorage `ks_vault_lock_minutes` (default 5)
 *  - sessionStorage `ks_vault_unlocked_at` (ISO timestamp) — wiped on tab close
 */
const LS_ENABLED = "ks_vault_lock_enabled";
const LS_MINUTES = "ks_vault_lock_minutes";
const SS_UNLOCKED = "ks_vault_unlocked_at";

export function isVaultLockEnabled() {
  try { return localStorage.getItem(LS_ENABLED) === "true"; }
  catch { return false; }
}
export function getVaultLockMinutes() {
  try { return Math.max(1, parseInt(localStorage.getItem(LS_MINUTES) || "5", 10)); }
  catch { return 5; }
}
export function isVaultUnlocked() {
  if (!isVaultLockEnabled()) return true;
  try {
    const raw = sessionStorage.getItem(SS_UNLOCKED);
    if (!raw) return false;
    const ms = Date.now() - Date.parse(raw);
    return ms >= 0 && ms < getVaultLockMinutes() * 60_000;
  } catch { return false; }
}
export function markVaultUnlocked() {
  try { sessionStorage.setItem(SS_UNLOCKED, new Date().toISOString()); }
  catch { /* noop */ }
}
export function lockVaultNow() {
  try { sessionStorage.removeItem(SS_UNLOCKED); }
  catch { /* noop */ }
}

/**
 * Gate wrapper — render `children` only when the Vault is unlocked.
 * Drop in around the Vault.jsx / Capsule.jsx content roots.
 */
export default function VaultLock({ children }) {
  const { t } = useI18n();
  const [unlocked, setUnlocked] = useState(() => isVaultUnlocked());
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Tick: every minute re-check the unlock window so a stale tab auto-locks
  useEffect(() => {
    if (!isVaultLockEnabled()) return;
    const id = setInterval(() => {
      if (!isVaultUnlocked()) setUnlocked(false);
    }, 30_000);
    return () => clearInterval(id);
  }, [unlocked]);

  // Reset the inactivity timer on every user interaction with the page
  useEffect(() => {
    if (!isVaultLockEnabled() || !unlocked) return;
    const bump = () => markVaultUnlocked();
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, bump));
  }, [unlocked]);

  if (unlocked) return children;

  const unlock = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!pwd) return setErr(t("vault_lock_pwd_required"));
    setBusy(true);
    try {
      await api.post("/auth/verify-password", { password: pwd });
      markVaultUnlocked();
      setPwd("");
      setUnlocked(true);
    } catch (e2) {
      const code = e2?.response?.status;
      if (code === 401) setErr(t("vault_lock_pwd_wrong"));
      else if (e2?.response?.data === "no_password_set") setErr(t("vault_lock_no_password"));
      else setErr(t("vault_lock_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-4"
      data-testid="vault-lock-screen"
    >
      <form
        onSubmit={unlock}
        className="glass-card rounded-md p-8 sm:p-10 max-w-md w-full text-center border border-gold-subtle"
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
      >
        <div className="flex justify-center mb-5">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "rgba(201,163,106,0.12)", border: "1px solid rgba(201,163,106,0.35)" }}
          >
            <Lock className="w-6 h-6 text-gold" strokeWidth={1.4} />
          </div>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-gold-light mb-2" data-testid="vault-lock-title">
          {t("vault_lock_title")}
        </h2>
        <p className="text-muted text-sm mb-6">
          {t("vault_lock_body").replace("{MIN}", String(getVaultLockMinutes()))}
        </p>
        <input
          type="password"
          className="input-field mb-3"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder={t("vault_lock_pwd_placeholder")}
          autoFocus
          autoComplete="current-password"
          data-testid="vault-lock-pwd"
        />
        {err && <p className="text-red-400 text-xs mb-3" data-testid="vault-lock-err">{err}</p>}
        <button
          type="submit"
          disabled={busy || !pwd}
          className="btn-gold w-full justify-center"
          data-testid="vault-lock-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          {busy ? t("vault_lock_unlocking") : t("vault_lock_cta")}
        </button>
      </form>
    </div>
  );
}
